import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { useCreateProject } from '@/hooks'
import type { CreateProjectRequest } from '@/types'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { ExtendedCreateProjectRequest, FormTeamMember } from '../form'
import { ProjectBasicInfoSection, ProjectTypeSection, TeamAssignmentSection } from '../form'

interface CreateProjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateProjectDialog({ open, onOpenChange }: CreateProjectDialogProps) {
  const createProject = useCreateProject()
  const methods = useForm<ExtendedCreateProjectRequest>({
    defaultValues: {
      name: '',
      description: '',
      client: '',
      type: '',
      industry: '',
      technologiesFrom: [],
      technologiesTo: [],
      teamMembers: [],
    },
  })

  const validateTeamMembers = (teamMembers: FormTeamMember[] | undefined): string | null => {
    if (!teamMembers || teamMembers.length === 0) {
      return 'At least one team member is required'
    }

    for (let i = 0; i < teamMembers.length; i++) {
      const member = teamMembers[i]

      if (!member.userId) {
        return `Team member ${i + 1}: User ID is required`
      }

      if (!member.roleIds || member.roleIds.length === 0) {
        return `Team member "${member.displayName}": At least one role is required`
      }

      for (const roleId of member.roleIds) {
        if (!roleId) {
          return `Team member "${member.displayName}": Role ID cannot be empty`
        }
      }
    }

    return null
  }

  const onSubmit = async (data: ExtendedCreateProjectRequest) => {
    const teamMembersError = validateTeamMembers(data.teamMembers)
    if (teamMembersError) {
      methods.setError('teamMembers', {
        type: 'manual',
        message: teamMembersError,
      })
      return
    }

    try {
      const name = data.name.trim()
      const client = data.client.trim()
      const description = data.description?.trim()

      const teamMembers = data.teamMembers.map(m => ({
        userId: m.userId,
        roleIds: m.roleIds,
      }))

      const payload: CreateProjectRequest = {
        name,
        client,
        type: data.type,
        industry: data.industry,
        ...(description ? { description } : {}),
        ...(data.technologiesFrom && data.technologiesFrom.length > 0
          ? { technologiesFrom: data.technologiesFrom }
          : {}),
        ...(data.technologiesTo && data.technologiesTo.length > 0
          ? { technologiesTo: data.technologiesTo }
          : {}),
        teamMembers,
      }
      await createProject.mutateAsync(payload)
      toast.success('Project created successfully')
      methods.reset()
      onOpenChange(false)
    } catch (error) {
      const apiError = error as {
        response?: {
          data?: {
            message?: string
            errors?: Array<{ field?: string; errorMessage?: string }>
          }
        }
      }

      const validationErrors = apiError?.response?.data?.errors
      if (validationErrors && validationErrors.length > 0) {
        const teamMemberError = validationErrors.find(e =>
          e.field?.toLowerCase().includes('teammember')
        )
        if (teamMemberError) {
          methods.setError('teamMembers', {
            type: 'manual',
            message: teamMemberError.errorMessage || 'Team member validation failed',
          })
        }

        const firstError = validationErrors[0]
        toast.error(firstError.errorMessage || 'Validation failed')
      } else {
        const message = apiError?.response?.data?.message || 'Failed to create project'
        toast.error(message)
      }
    }
  }

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      methods.reset()
    }
    onOpenChange(value)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Create New Project</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-input-background">
          <div className="max-w-4xl mx-auto py-8 px-6">
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <ProjectBasicInfoSection />
                    <Separator />
                    <ProjectTypeSection />
                    <Separator />
                    <TeamAssignmentSection />
                  </CardContent>
                </Card>
                <div className="mt-6 flex justify-end gap-3">
                  <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createProject.isPending}
                  >
                    {createProject.isPending ? 'Creating...' : 'Create Project'}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
